import { Button, Empty, Form, message, Modal, Spin, Typography } from 'antd';
import { useServicio, useUpdateServicio } from '../api/useServicios';
import { usePlanesList } from '../../catalogos/api/useCatalogos';
import { useT } from '../../../locales';
import { ServicioForm } from './ServicioForm';
import { formatCurrency } from '../../../lib/format';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ServicioEditModalProps {
  id: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ServicioEditModal({ id, onClose, onSuccess }: ServicioEditModalProps) {
  const t = useT();
  const [form] = Form.useForm();
  const { data: servicio, isLoading, error } = useServicio(id ?? 0);
  const updateServicio = useUpdateServicio();
  // Lista completa de planes (catálogo chico) para resolver nombre/precio del
  // plan nuevo en la confirmación del cambio.
  const { data: planes } = usePlanesList(undefined);

  const nuevoPlanId = Form.useWatch('idPlan', form) as number | null | undefined;
  const planNuevo = planes?.find((p) => p.id === nuevoPlanId);

  const ejecutarUpdate = async (values: Record<string, unknown>) => {
    if (!servicio) return;
    try {
      await updateServicio.mutateAsync({ id: servicio.id, dto: values });
      message.success(t('pages.servicios.editSuccess'));
      onClose();
      onSuccess?.();
    } catch {
      message.error(t('pages.servicios.editError'));
    }
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!servicio) return;
    const cambiaPlan = values.idPlan !== undefined && Number(values.idPlan) !== servicio.idPlan;
    if (!cambiaPlan) {
      await ejecutarUpdate(values);
      return;
    }

    // Cambio de plan: confirmación explícita con preview del precio y la fecha
    // de efecto (1° del próximo mes — regla del backend, servicio_plan_historial).
    const nombreActual = servicio.plan?.nombre ?? `#${servicio.idPlan}`;
    const precioActual = servicio.plan?.precio
      ? formatCurrency(Number(servicio.plan.precio))
      : null;
    const nombreNuevo = planNuevo?.nombre ?? `#${values.idPlan}`;
    const precioNuevo = planNuevo?.precio ? formatCurrency(Number(planNuevo.precio)) : null;
    const fechaEfecto = dayjs().add(1, 'month').startOf('month').format('DD/MM/YYYY');

    Modal.confirm({
      title: t('pages.servicios.planCambioTitle'),
      content: (
        <div>
          <div style={{ marginBottom: 10 }}>
            <Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 2 }}
            >
              {t('pages.servicios.planCambioActual')}
            </Text>
            <Text style={{ fontSize: 14, fontWeight: 500 }}>{nombreActual}</Text>
            {precioActual && (
              <Text type="secondary" style={{ fontSize: 14, marginLeft: 6 }}>
                {precioActual}
              </Text>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginBottom: 2 }}
            >
              {t('pages.servicios.planCambioNuevo')}
            </Text>
            <Text style={{ fontSize: 14, fontWeight: 600 }}>{nombreNuevo}</Text>
            {precioNuevo && (
              <Text type="secondary" style={{ fontSize: 14, marginLeft: 6 }}>
                {precioNuevo}
              </Text>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
            {t('pages.servicios.planCambioEfecto', { fecha: fechaEfecto })}
          </Text>
        </div>
      ),
      okText: t('pages.servicios.planCambioOk'),
      cancelText: t('common.cancel'),
      onOk: () => ejecutarUpdate(values),
    });
  };

  return (
    <Modal
      title={t('pages.servicios.edit')}
      open={id !== null}
      onCancel={onClose}
      destroyOnHidden
      centered
      width={640}
      styles={{
        body: {
          maxHeight: 'min(75vh, 675px)',
          overflowY: 'auto',
        },
      }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={updateServicio.isPending}
          onClick={() => form.submit()}
        >
          {t('common.saveChanges')}
        </Button>,
      ]}
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : error || !servicio ? (
        <Empty
          description={
            <div>
              <div>{t('pages.servicios.notFound')}</div>
              <div style={{ fontSize: 13 }}>{t('pages.servicios.notFoundDetail')}</div>
            </div>
          }
        />
      ) : (
        <ServicioForm
          mode="edit"
          form={form}
          hideSubmit
          initialValues={servicio}
          onSubmit={handleSubmit}
          loading={updateServicio.isPending}
        />
      )}
    </Modal>
  );
}
